import { FC, useState } from 'react';
import { useIntl } from 'react-intl';
import { Button, MenuSeparator, TextField } from '@getgo/chameleon-web-react-wrapper';
import { Form, Formik } from 'formik';

import DirectDebitPadMandate from 'components/direct-debit-pad-mandate';
import { useAppDispatch, useAppSelector } from 'hooks';
import { showErrorSnack } from 'modules/error';
import { postPaymentMethods, setChosenPaymentMethodKey, setPayNowIntent } from 'modules/payment-methods';
import { sessionIntent, sessionSuccessRedirectUrl } from 'modules/session-details';
import Track, { PaymentMethodEventNames } from 'modules/tracking';
import { postTransactionAddDD } from 'modules/transaction-dd';
import { AddDDPayload } from 'types/transaction-dd';
import {
  COPAS_PAYMENT_INTENT,
  COPAS_PAYMENT_STATUS,
  DD_TYPES,
  SEARCH_PARAMS,
  TRACKING_EVENT_STATUS,
} from 'utils/constants';
import { getPadFormValidationSchema } from 'utils/direct-debit-utils';
import st from 'utils/shared-translations';

import './direct-debit-pad-form.css';

const DirectDebitPadForm: FC = (): JSX.Element => {
  const intl = useIntl();
  const dispatch = useAppDispatch();

  const [isDDMandate, setDDMandate] = useState(false);
  const [isDefault, setDefault] = useState(false);

  const locale = localStorage.getItem(SEARCH_PARAMS.locale);

  const selectedSuccessRedirectUrl = useAppSelector(sessionSuccessRedirectUrl);
  const selectedSessionIntent = useAppSelector(sessionIntent);

  const handleOnSubmit = (values, { setSubmitting }) => {
    Track(PaymentMethodEventNames.directDebitCTA, {
      status: TRACKING_EVENT_STATUS.click,
      isDefault,
      paymentType: DD_TYPES.pad,
    });

    const payload: AddDDPayload = {
      ...values,
      language: locale?.split('_')?.[0] || 'en',
      setAsDefault: isDefault,
    };

    dispatch(postTransactionAddDD(payload))
      .unwrap()
      .then((resp) => {
        if (resp.status === COPAS_PAYMENT_STATUS.FAILED) {
          Track(PaymentMethodEventNames.directDebitCTA, {
            status: TRACKING_EVENT_STATUS.copasFailure,
            paymentType: DD_TYPES.pad,
          });
          setSubmitting(false);
          dispatch(showErrorSnack(st['alert.error.paymentmethod.general.failure']));
          return;
        }
        if (selectedSessionIntent === COPAS_PAYMENT_INTENT.PAYMENT_OR_PAYMENT_METHOD_CHANGE) {
          dispatch(postPaymentMethods()).then(() => {
            dispatch(setChosenPaymentMethodKey(resp.paymentMethodKey));
            dispatch(setPayNowIntent(''));
          });
        } else {
          localStorage.clear();
          window.location.assign(selectedSuccessRedirectUrl);
        }
      })
      .catch(() => {
        setDDMandate(false);
        setSubmitting(false);
        setDefault(false);
        dispatch(showErrorSnack(st['alert.error.paymentmethod.general.failure']));
      });
    return;
  };

  return (
    <Formik
      initialValues={{ accountNumber: '', branchCode: '', bankCode: '' }}
      validationSchema={getPadFormValidationSchema(intl)}
      onSubmit={handleOnSubmit}
    >
      {({ values, isValid, errors, handleChange, isSubmitting, dirty, touched, setFieldTouched }) => (
        <Form spellCheck={false}>
          <div className="direct-debit-pad-form">
            <TextField
              name="accountNumber"
              fieldsize="medium"
              value={values.accountNumber}
              onBlur={() => setFieldTouched('accountNumber', true)}
              labelId="account-number"
              onChange={handleChange}
              disabled={isDDMandate}
              helperText={touched.accountNumber && errors.accountNumber}
              error={touched.accountNumber && !!errors.accountNumber}
              fullwidth
            >
              {intl.formatMessage(st['dd.account.number'])}
            </TextField>
            <div className="direct-debit-pad-form__input-group">
              <TextField
                name="bankCode"
                fieldsize="medium"
                value={values.bankCode}
                onBlur={() => setFieldTouched('bankCode', true)}
                labelId="bank-code"
                onChange={handleChange}
                disabled={isDDMandate}
                helperText={touched.bankCode && errors.bankCode}
                error={touched.bankCode && !!errors.bankCode}
                fullwidth
              >
                {intl.formatMessage(st['dd.bank.code'])}
              </TextField>
              <TextField
                name="branchCode"
                fieldsize="medium"
                value={values.branchCode}
                onBlur={() => setFieldTouched('branchCode', true)}
                labelId="branch-code"
                onChange={handleChange}
                disabled={isDDMandate}
                helperText={touched.branchCode && errors.branchCode}
                error={touched.branchCode && !!errors.branchCode}
                fullwidth
              >
                {intl.formatMessage(st['dd.branch.code'])}
              </TextField>
            </div>
            <Button
              className="direct-debit-pad-form__cta"
              onClick={() => setDDMandate(true)}
              disabled={isDDMandate || !isValid || isSubmitting || !dirty}
            >
              {intl.formatMessage(st['dd.form.cta.validate'])}
            </Button>
          </div>
          {isDDMandate && (
            <>
              <MenuSeparator className="direct-debit-pad-form__menu-separator" />
              <DirectDebitPadMandate
                handleCancelMandate={setDDMandate}
                accountNumber={values.accountNumber}
                branchCode={values.branchCode}
                bankCode={values.bankCode}
                isSubmitting={isSubmitting}
                isDefault={isDefault}
                setDefault={setDefault}
                isValid={isValid}
                dirty={dirty}
              />
            </>
          )}
        </Form>
      )}
    </Formik>
  );
};

export default DirectDebitPadForm;
